// مشاهده و پیگیری ارجاع و دستور بستری تامین (AP-MED-03؛ US-MED-098، 100؛ SCR-STAFF-705 و SCR-STAFF-716).
//
// نشست بیمه‌گر **خودِ کاربر** شرط است (BR-INT-108): پذیرش «ارجاع‌های باز بیمار» و «دستورهای بستری مرکز» را می‌بیند و
// سرویس‌های پزشک (کد رهگیری، بازخوردها، ۳ روز اخیر) با نشست منشی `PHX-INT-0347` می‌گیرند. نتیجه‌ها Clinical‌اند و هر
// مشاهده در سرور با `RecordView` ثبت می‌شود (BR-MED-104).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type MedInsurerAdmissionOrderItemView,
  type MedInsurerAdmissionOrderListView,
  type MedInsurerFeedbackDetailView,
  type MedInsurerFeedbackEntryView,
  type MedInsurerFeedbackListView,
  type MedInsurerReferralItemView,
  type MedInsurerReferralListView,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ListPage, SampleBadge, faDigits, toJalali, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { referralKeys, SCR_STAFF_716 } from './referralNational';

function sessionProblem(error: unknown): boolean {
  return isApiError(error) && error.code === 'PHX-INT-0347';
}

function credentialProblem(error: unknown): boolean {
  return isApiError(error) && error.code === 'PHX-MED-0302';
}

/** هشدار نشست یا اعتبارنامه بیمه‌گر (AC-MED-100.1، AC-INT-041.2). */
function InsurerSessionAlert({ error }: { error: unknown }) {
  const { t } = useTranslation('med');

  if (sessionProblem(error)) {
    return (
      <Alert severity="warning" data-testid="med-insurer-session-alert">
        {t('med:insurer_referral.session_required')}
      </Alert>
    );
  }

  if (credentialProblem(error)) {
    return (
      <Alert severity="warning" data-testid="med-insurer-credential-alert">
        {t('med:insurer_referral.credential_required')}
      </Alert>
    );
  }

  return null;
}

/**
 * ارجاع‌های باز یک بیمار نزد تامین (AC-MED-100.1): پذیرش متخصص هنگام پذیرش آن را می‌بیند و شمار ارجاع باز با تخصص
 * پزشک انتخابی را هم دارد.
 */
export function InsurerOpenReferralsPanel({ patientId, providerId }: { patientId: string; providerId?: string }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const canView = access.can('med.referral.view');

  const referrals = useQuery({
    queryKey: referralKeys.insurerOpen(session?.tenantId, patientId, providerId ?? '-'),
    enabled: canView,
    queryFn: async (): Promise<MedInsurerReferralListView> =>
      (await unwrap(
        tenantApi.GET('/api/v1/medical-orders/insurer-referrals/open', {
          params: { query: { patientId, providerId: providerId || undefined } },
        }),
      )) as MedInsurerReferralListView,
  });

  if (!canView) {
    return null;
  }

  const columns: DataTableColumn<MedInsurerReferralItemView>[] = [
    { id: 'code', headerKey: 'med:insurer_referral.tracking_code', cell: (row) => <span>{row.trackingCode ?? '—'}</span> },
    { id: 'date', headerKey: 'med:insurer_referral.referred_on', cell: (row) => <span>{row.referredOn ? toJalali(row.referredOn) : '—'}</span> },
    { id: 'from', headerKey: 'med:insurer_referral.from', cell: (row) => <span>{row.fromProviderName ?? row.fromSpecialtyCode ?? '—'}</span> },
    { id: 'to', headerKey: 'med:insurer_referral.to', cell: (row) => <span>{row.toSpecialtyCode ?? '—'}</span> },
    {
      id: 'family',
      headerKey: 'med:insurer_referral.family_doctor',
      cell: (row) =>
        row.fromFamilyDoctor ? <Chip size="small" label={t('med:insurer_referral.family_doctor')} data-testid="med-insurer-family" /> : null,
    },
  ];

  return (
    <Stack spacing={2} data-testid="med-insurer-open-panel">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1">{t('med:insurer_referral.open_title')}</Typography>
        {referrals.data?.isSample ? <SampleBadge /> : null}
        {referrals.data?.openCount != null ? (
          <Chip size="small" color="primary" label={t('med:insurer_referral.open_count', { count: referrals.data.openCount })} data-testid="med-insurer-open-count" />
        ) : null}
      </Stack>
      <InsurerSessionAlert error={referrals.error} />
      <DataTable
        rows={referrals.data?.items}
        columns={columns}
        getRowId={(row) => row.referralId ?? row.trackingCode ?? ''}
        isLoading={referrals.isLoading}
        error={sessionProblem(referrals.error) || credentialProblem(referrals.error) ? undefined : referrals.error}
        onRetry={() => void referrals.refetch()}
        emptyTitleKey="med:insurer_referral.open_empty"
        captionKey="med:insurer_referral.open_title"
      />
    </Stack>
  );
}

/**
 * پیگیری ارجاع تامین برای پزشک (AC-MED-100.2): جزئیات با کد رهگیری، فهرست بازخوردها (`id` صفر = خود بازخورد) و
 * ارجاع‌های ۳ روز اخیر خودش.
 */
export function InsurerReferralTrackingPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const canView = access.can('med.referral.view');

  const found = useQuery({
    queryKey: ['medorders', session?.tenantId, 'insurer-referral-by-code', patientId, submitted] as const,
    enabled: canView && submitted.length > 2,
    queryFn: async (): Promise<MedInsurerReferralListView> =>
      (await unwrap(
        tenantApi.GET('/api/v1/medical-orders/insurer-referrals/by-code', {
          params: { query: { patientId, trackingCode: submitted } },
        }),
      )) as MedInsurerReferralListView,
  });

  const recent = useQuery({
    queryKey: ['medorders', session?.tenantId, 'insurer-referral-recent'] as const,
    enabled: canView,
    queryFn: async (): Promise<MedInsurerReferralListView> =>
      (await unwrap(tenantApi.GET('/api/v1/medical-orders/insurer-referrals/recent', {}))) as MedInsurerReferralListView,
  });

  const referralId = found.data?.items[0]?.referralId ?? null;

  const feedbacks = useQuery({
    queryKey: ['medorders', session?.tenantId, 'insurer-referral-feedbacks', referralId ?? '-'] as const,
    enabled: canView && Boolean(referralId),
    queryFn: async (): Promise<MedInsurerFeedbackListView> =>
      (await unwrap(
        tenantApi.GET('/api/v1/medical-orders/insurer-referrals/{referralId}/feedbacks', {
          params: { path: { referralId: referralId! } },
        }),
      )) as MedInsurerFeedbackListView,
  });

  const detail = useQuery({
    queryKey: ['medorders', session?.tenantId, 'insurer-referral-feedback', feedbackId ?? '-'] as const,
    enabled: canView && Boolean(feedbackId),
    queryFn: async (): Promise<MedInsurerFeedbackDetailView> =>
      (await unwrap(
        tenantApi.GET('/api/v1/medical-orders/insurer-referrals/feedbacks/{id}', {
          params: { path: { id: feedbackId! }, query: { patientId, masterParent: referralId || undefined } },
        }),
      )) as MedInsurerFeedbackDetailView,
  });

  if (!canView) {
    return null;
  }

  const entryColumns: DataTableColumn<MedInsurerFeedbackEntryView>[] = [
    {
      id: 'kind',
      headerKey: 'med:insurer_referral.entry_kind',
      cell: (row) => (
        <span data-testid={`med-insurer-entry-${row.id}`}>
          {row.id === '0' ? t('med:insurer_referral.entry_feedback') : t('med:insurer_referral.entry_prescription')}
        </span>
      ),
    },
    { id: 'code', headerKey: 'med:insurer_referral.tracking_code', cell: (row) => <span>{row.trackingCode ?? '—'}</span> },
    { id: 'date', headerKey: 'med:insurer_referral.recorded_on', cell: (row) => <span>{row.recordedOn ? toJalali(row.recordedOn) : '—'}</span> },
    {
      id: 'open',
      headerKey: 'med:insurer_referral.entry_open',
      cell: (row) => (
        <Button size="small" variant="outlined" onClick={() => setFeedbackId(row.id)} data-testid={`med-insurer-entry-open-${row.id}`}>
          {t('med:insurer_referral.entry_open')}
        </Button>
      ),
    },
  ];

  return (
    <Stack spacing={2} data-testid="med-insurer-tracking-panel">
      <Typography variant="subtitle1">{t('med:insurer_referral.tracking_title')}</Typography>
      <InsurerSessionAlert error={found.error ?? recent.error} />
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          label={t('med:insurer_referral.tracking_code')}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          inputProps={{ 'data-testid': 'med-insurer-code-input' }}
          sx={{ maxWidth: 280 }}
        />
        <Button variant="contained" onClick={() => setSubmitted(code.trim())} data-testid="med-insurer-code-search">
          {t('med:insurer_referral.find')}
        </Button>
      </Stack>
      {found.data ? (
        found.data.items.length > 0 ? (
          <Stack spacing={0.5} data-testid="med-insurer-found">
            <Typography variant="body2">
              {t('med:insurer_referral.from')}: {found.data.items[0]!.fromProviderName ?? '—'}
            </Typography>
            <Typography variant="body2">
              {t('med:insurer_referral.message')}: {found.data.items[0]!.message ?? '—'}
            </Typography>
            <Typography variant="body2">
              {t('med:insurer_referral.complaints')}: {found.data.items[0]!.complaints.join(t('common:list_separator')) || '—'}
            </Typography>
            <Typography variant="body2">
              {t('med:insurer_referral.diagnoses')}: {found.data.items[0]!.diagnoses.join(t('common:list_separator')) || '—'}
            </Typography>
          </Stack>
        ) : (
          <Alert severity="info" data-testid="med-insurer-not-found">
            {t('med:insurer_referral.not_found')}
          </Alert>
        )
      ) : null}
      {referralId ? (
        <DataTable
          rows={feedbacks.data?.entries}
          columns={entryColumns}
          getRowId={(row) => row.id}
          isLoading={feedbacks.isLoading}
          error={feedbacks.error}
          onRetry={() => void feedbacks.refetch()}
          emptyTitleKey="med:insurer_referral.entries_empty"
          captionKey="med:insurer_referral.entries_title"
        />
      ) : null}
      {detail.data ? (
        <Stack spacing={0.5} data-testid="med-insurer-feedback-detail">
          {detail.data.isSample ? <SampleBadge /> : null}
          <Typography variant="body2">
            {t('med:insurer_referral.comments')}: {detail.data.comments ?? '—'}
          </Typography>
          {detail.data.answers.map((answer, index) => (
            <Typography key={index} variant="body2">
              {answer.question}: {answer.answer} {answer.comment ? `· ${answer.comment}` : ''}
            </Typography>
          ))}
          {detail.data.items.map((item, index) => (
            <Typography key={index} variant="body2" data-testid="med-insurer-feedback-item">
              {item.serviceName ?? item.serviceCode} · {faDigits(item.quantity ?? 0)}
            </Typography>
          ))}
        </Stack>
      ) : null}
      <Typography variant="subtitle2">{t('med:insurer_referral.recent_title')}</Typography>
      <DataTable
        rows={recent.data?.items}
        columns={[
          { id: 'code', headerKey: 'med:insurer_referral.tracking_code', cell: (row) => <span>{row.trackingCode ?? '—'}</span> },
          { id: 'date', headerKey: 'med:insurer_referral.referred_on', cell: (row) => <span>{row.referredOn ? toJalali(row.referredOn) : '—'}</span> },
          { id: 'to', headerKey: 'med:insurer_referral.to', cell: (row) => <span>{row.toSpecialtyCode ?? '—'}</span> },
        ]}
        getRowId={(row) => row.trackingCode ?? ''}
        isLoading={recent.isLoading}
        error={sessionProblem(recent.error) ? undefined : recent.error}
        onRetry={() => void recent.refetch()}
        emptyTitleKey="med:insurer_referral.recent_empty"
        captionKey="med:insurer_referral.recent_title"
      />
    </Stack>
  );
}

/** SCR-STAFF-716 — دستورهای بستری تامین مرکز یا یک بیمار (AC-MED-098.2). */
export function InsurerAdmissionOrdersPage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const [patientId, setPatientId] = useState('');

  const orders = useQuery({
    queryKey: referralKeys.insurerAdmissions(session?.tenantId, patientId || '-'),
    queryFn: async (): Promise<MedInsurerAdmissionOrderListView> =>
      (await unwrap(
        tenantApi.GET('/api/v1/medical-orders/insurer-admission-orders', {
          params: { query: { patientId: patientId || undefined } },
        }),
      )) as MedInsurerAdmissionOrderListView,
  });

  const columns: DataTableColumn<MedInsurerAdmissionOrderItemView>[] = [
    { id: 'code', headerKey: 'med:insurer_referral.tracking_code', cell: (row) => <span>{row.trackingCode ?? '—'}</span> },
    { id: 'patient', headerKey: 'med:admission.patient', cell: (row) => <span>{row.patientName ?? '—'}</span> },
    { id: 'specialty', headerKey: 'med:insurer_referral.to', cell: (row) => <span>{row.specialtyCodes.join(t('common:list_separator')) || '—'}</span> },
    { id: 'diagnoses', headerKey: 'med:referral_national.diagnosis', cell: (row) => <span>{row.diagnoses.join(t('common:list_separator')) || '—'}</span> },
    { id: 'admit', headerKey: 'med:admission.admit_on', cell: (row) => <span>{row.admitOn ? toJalali(row.admitOn) : '—'}</span> },
    {
      id: 'family',
      headerKey: 'med:insurer_referral.family_doctor',
      cell: (row) =>
        row.fromFamilyDoctor ? <Chip size="small" label={t('med:insurer_referral.family_doctor')} data-testid="med-admission-family" /> : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_716}
      titleKey="med:admission.page_title"
      subtitleKey="med:admission.page_subtitle"
      badges={orders.data?.isSample ? <SampleBadge /> : undefined}
      filters={
        <TextField
          label={t('med:admission.patient')}
          helperText={t('med:admission.patient_hint')}
          value={patientId}
          onChange={(event) => setPatientId(event.target.value)}
          inputProps={{ 'data-testid': 'med-admission-patient-filter' }}
          sx={{ minWidth: 280 }}
        />
      }
    >
      <Stack spacing={2}>
        <InsurerSessionAlert error={orders.error} />
        <DataTable
          rows={orders.data?.items}
          columns={columns}
          getRowId={(row) => row.trackingCode ?? ''}
          isLoading={orders.isLoading}
          error={sessionProblem(orders.error) || credentialProblem(orders.error) ? undefined : orders.error}
          onRetry={() => void orders.refetch()}
          emptyTitleKey="med:admission.page_empty"
          captionKey="med:admission.page_title"
        />
      </Stack>
    </ListPage>
  );
}
